import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "./Constants";
import { parseISO,format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import moment from 'moment';


const SessionsList2Tutor = (props) => {
  const id = props.id;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("scheduled"); // all, scheduled, completed, cancelled, no_show

  // Fetch sessions from API
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/sessions`);
        const filteredData = res.data.filter(
          (session) => session.enrollment?.tutor_id === id
        );
        setSessions(filteredData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching sessions", err);
        setError("Failed to load sessions");
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  // Filter sessions based on status
  const filteredSessions =
    filter === "all"
      ? sessions
      : sessions.filter((session) => session.status === filter);

  // Function to format date and time
  const formatDateTime = (dateTimeString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateTimeString).toLocaleDateString("en-US", options);
  };

  // Function to calculate end time
  const calculateEndTime = (startTime, duration) => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + duration * 60000);
    return end.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "scheduled":
        return "badge-info";
      case "completed":
        return "badge-success";
      case "cancelled":
        return "badge-error";
      case "no_show":
        return "badge-warning";
      default:
        return "badge-neutral";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
        <span className="ml-2">Loading sessions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error shadow-lg max-w-2xl mx-auto my-8">
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current flex-shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold text-center mb-8 text-white bg-secondary">
        Class Rooms
      </h1>

      {/* Filter Controls */}
      <div className="flex justify-center mb-6">
        <div className="tabs tabs-boxed bg-info text-white rounded-2xl">
          <a
            className={`tab ${filter === "scheduled" ? "tab-active" : ""}`}
            onClick={() => setFilter("scheduled")}
          >
            Scheduled
          </a>
          <a
            className={`tab ${filter === "completed" ? "tab-active" : ""}`}
            onClick={() => setFilter("completed")}
          >
            Completed
          </a>
          <a
            className={`tab ${filter === "cancelled" ? "tab-active" : ""}`}
            onClick={() => setFilter("cancelled")}
          >
            Cancelled
          </a>
          <a
            className={`tab ${filter === "no_show" ? "tab-active" : ""}`}
            onClick={() => setFilter("no_show")}
          >
            No Show
          </a>
          <a
            className={`tab ${filter === "all" ? "tab-active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All Sessions
          </a>
        </div>
      </div>

      {filteredSessions.length === 0 ? (
        <div className="alert alert-info shadow-lg max-w-2xl mx-auto">
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="stroke-current flex-shrink-0 h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>No sessions found.</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="card-body">
                {/* Session Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="card-title">Session #{session.id}</h2>
                    <p className="text-sm text-gray-500">
                      Enrollment:{" "}
                      {session.enrollment?.course_name ||
                        `ID: ${session.enrollment_id}`}
                    </p>
                  </div>
                  <span
                    className={`badge ${getStatusBadgeColor(
                      session.status
                    )} badge-lg`}
                  >
                    {session.status.charAt(0).toUpperCase() +
                      session.status.slice(1)}
                  </span>
                </div>

                <div className="divider my-2"></div>

                {/* Session Details */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-semibold">Dates & Time:</span>
                    <span>{formatDateTime(session.scheduled_time)}</span>

                   {/* <span>{session.scheduled_time}</span> */}
                  </div>

                  <div className="flex justify-between">
                    <span className="font-semibold">Duration:</span>
                    <span>{session.duration_minutes} minutes</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="font-semibold">Ends at:</span>
                    <span>
                      {calculateEndTime(
                        session.scheduled_time,
                        session.duration_minutes
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="font-semibold">Student:</span>
                    <span>
                      {session.enrollment?.student?.name ||
                        `ID: ${session.enrollment.student.id}`}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="font-semibold">Tutor:</span>
                    <span>
                      {session.enrollment?.tutor?.name ||
                        `ID: ${session.enrollment?.tutor_id}`}
                    </span>
                  </div>

                  {session.meeting_link && (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Meeting Link:</span>
                      <a
                        href={session.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link link-primary"
                      >
                        Join Meeting
                      </a>
                    </div>
                  )}
                </div>

                <div className="divider my-2"></div>

                {/* Session Metadata */}
                <div className="flex justify-between text-sm text-gray-500">
                  <span>
                    Created: {new Date(session.created_at).toLocaleDateString()}
                  </span>
                  <span>
                    Updated: {new Date(session.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionsList2Tutor;
